import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Type, AlignLeft, Hash, CheckSquare, CircleDot, ChevronDown, SlidersHorizontal,
  Paperclip, Calendar, Clock, Coins, GripVertical, Trash2, Plus, Save, Eye, Pencil,
  Download, Upload, Copy, Loader2, AlertTriangle, FileStack, ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_TYPES = [
  { type: 'text', label: 'Texte', icon: Type },
  { type: 'textarea', label: 'Texte long', icon: AlignLeft },
  { type: 'number', label: 'Nombre', icon: Hash },
  { type: 'checkbox', label: 'Case a cocher', icon: CheckSquare },
  { type: 'radio', label: 'Boutons radio', icon: CircleDot },
  { type: 'select', label: 'Liste deroulante', icon: ChevronDown },
  { type: 'slider', label: 'Echelle (slider)', icon: SlidersHorizontal },
  { type: 'file', label: 'Fichier', icon: Paperclip },
  { type: 'date', label: 'Date', icon: Calendar },
  { type: 'duration', label: 'Duree', icon: Clock },
  { type: 'montant_total', label: 'Montant total', icon: Coins },
];
const TYPE_LABEL = Object.fromEntries(FIELD_TYPES.map((t) => [t.type, t.label]));
const DURATION_UNITS = ['Secondes', 'Minutes', 'Heures', 'Jours', 'Semaines', 'Mois', 'Annees'];

const uid = (p) => `${p}_${Math.random().toString(36).slice(2, 10)}`;
const slugKey = (label) => label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || uid('field');

const montantPlaced = (steps) =>
  (steps || []).some((s) => (s.fields || []).some((f) => f.key === 'montant_total' || f.type === 'montant_total'));

export const AdminFDVBuilder = () => {
  const { isGroupSA } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [agencyId, setAgencyId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  // editor state
  const [editing, setEditing] = useState(null); // {template_id, name, is_active, agency_id}
  const [steps, setSteps] = useState([]);
  const [selected, setSelected] = useState(null); // {stepId, fieldId?} or {stepId}
  const [tab, setTab] = useState('build'); // build | preview
  const [saving, setSaving] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupAgency, setDupAgency] = useState('');

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/agencies`, { credentials: 'include' });
        if (r.ok) {
          const data = await r.json();
          setAgencies(data);
          if (data.length) setAgencyId(data[0].agency_id);
        }
      } catch (e) { /* noop */ }
    })();
  }, []);

  const fetchTemplates = useCallback(async (aid) => {
    if (!aid) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/fdv-templates?agency_id=${aid}`, { credentials: 'include' });
      if (r.ok) setTemplates(await r.json());
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (agencyId) fetchTemplates(agencyId); }, [agencyId, fetchTemplates]);

  const openEditor = async (templateId) => {
    try {
      const r = await fetch(`${API}/fdv-templates/${templateId}`, { credentials: 'include' });
      if (!r.ok) { toast.error('Template introuvable'); return; }
      const t = await r.json();
      setEditing({ template_id: t.template_id, name: t.name, is_active: t.is_active, agency_id: t.agency_id });
      setSteps((t.steps || []).sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((s) => ({ ...s, fields: (s.fields || []).sort((a, b) => (a.order || 0) - (b.order || 0)) })));
      setSelected(null);
      setTab('build');
    } catch (e) { toast.error('Erreur de chargement'); }
  };

  const createTemplate = async () => {
    try {
      const r = await fetch(`${API}/fdv-templates`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agency_id: agencyId, name: 'Nouveau template', steps: [] }),
      });
      if (r.ok) {
        const t = await r.json();
        await fetchTemplates(agencyId);
        openEditor(t.template_id);
        toast.success('Template cree');
      } else {
        toast.error((await r.json()).detail || 'Erreur');
      }
    } catch (e) { toast.error('Erreur de creation'); }
  };

  const deleteTemplate = async (t) => {
    if ((t.assigned_agencies || []).length > 0) { toast.error('Impossible de supprimer un formulaire attribue a une filiale'); return; }
    if (!window.confirm(`Supprimer "${t.name}" ?`)) return;
    const r = await fetch(`${API}/fdv-templates/${t.template_id}`, { method: 'DELETE', credentials: 'include' });
    if (r.ok) { toast.success('Supprime'); fetchTemplates(agencyId); }
    else toast.error((await r.json()).detail || 'Erreur');
  };

  // ---- editor mutations ----
  const normalizeOrders = (arr) => arr.map((it, i) => ({ ...it, order: i + 1 }));

  const addStep = () => {
    const step = { step_id: uid('stp'), title: `Etape ${steps.length + 1}`, description: '', order: steps.length + 1, fields: [] };
    setSteps((prev) => [...prev, step]);
    setSelected({ stepId: step.step_id });
  };
  const removeStep = (stepId) => {
    setSteps((prev) => normalizeOrders(prev.filter((s) => s.step_id !== stepId)));
    setSelected(null);
  };
  const updateStep = (stepId, patch) => {
    setSteps((prev) => prev.map((s) => (s.step_id === stepId ? { ...s, ...patch } : s)));
  };

  const addField = (type) => {
    let stepId = selected?.stepId;
    if (!stepId) {
      if (steps.length === 0) { toast.info('Ajoutez d\'abord une etape'); return; }
      stepId = steps[steps.length - 1].step_id;
    }
    if (type === 'montant_total' && montantPlaced(steps)) {
      toast.info('Le champ montant_total est deja present');
      return;
    }
    const isMontant = type === 'montant_total';
    const field = {
      field_id: uid('fld'),
      key: isMontant ? 'montant_total' : uid('field'),
      type,
      label: isMontant ? 'Montant total TTC (EUR)' : TYPE_LABEL[type],
      required: isMontant,
      order: 0,
      options: (type === 'select' || type === 'radio' || type === 'checkbox') ? ['Option 1', 'Option 2'] : undefined,
      min: type === 'slider' ? 0 : undefined,
      max: type === 'slider' ? 100 : undefined,
      step: type === 'slider' ? 1 : undefined,
      multiple: false,
      max_selections: type === 'checkbox' ? null : undefined,
      units: type === 'duration' ? [...DURATION_UNITS] : undefined,
      help_text: isMontant ? 'Ce montant sert au calcul des commissions.' : undefined,
    };
    setSteps((prev) => prev.map((s) => (s.step_id === stepId
      ? { ...s, fields: normalizeOrders([...(s.fields || []), field]) } : s)));
    setSelected({ stepId, fieldId: field.field_id });
  };
  const removeField = (stepId, fieldId) => {
    setSteps((prev) => prev.map((s) => (s.step_id === stepId
      ? { ...s, fields: normalizeOrders((s.fields || []).filter((f) => f.field_id !== fieldId)) } : s)));
    setSelected({ stepId });
  };
  const updateField = (stepId, fieldId, patch) => {
    setSteps((prev) => prev.map((s) => (s.step_id === stepId
      ? { ...s, fields: (s.fields || []).map((f) => (f.field_id === fieldId ? { ...f, ...patch } : f)) } : s)));
  };
  const moveFieldToStep = (fromStep, fieldId, toStep) => {
    if (fromStep === toStep) return;
    let moved = null;
    setSteps((prev) => {
      const next = prev.map((s) => {
        if (s.step_id === fromStep) {
          moved = (s.fields || []).find((f) => f.field_id === fieldId);
          return { ...s, fields: normalizeOrders((s.fields || []).filter((f) => f.field_id !== fieldId)) };
        }
        return s;
      });
      return next.map((s) => (s.step_id === toStep && moved
        ? { ...s, fields: normalizeOrders([...(s.fields || []), moved]) } : s));
    });
    setSelected({ stepId: toStep, fieldId });
  };

  const onFieldDragEnd = (stepId) => (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSteps((prev) => prev.map((s) => {
      if (s.step_id !== stepId) return s;
      const ids = s.fields.map((f) => f.field_id);
      const oldIdx = ids.indexOf(active.id); const newIdx = ids.indexOf(over.id);
      if (oldIdx < 0 || newIdx < 0) return s;
      return { ...s, fields: normalizeOrders(arrayMove(s.fields, oldIdx, newIdx)) };
    }));
  };
  const onStepDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSteps((prev) => {
      const ids = prev.map((s) => s.step_id);
      const oldIdx = ids.indexOf(active.id); const newIdx = ids.indexOf(over.id);
      if (oldIdx < 0 || newIdx < 0) return prev;
      return normalizeOrders(arrayMove(prev, oldIdx, newIdx));
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payloadSteps = steps.map((s, i) => ({
        ...s, order: i + 1,
        fields: (s.fields || []).map((f, j) => ({ ...f, order: j + 1 })),
      }));
      const r = await fetch(`${API}/fdv-templates/${editing.template_id}`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editing.name, steps: payloadSteps }),
      });
      if (r.ok) { toast.success('Enregistre'); fetchTemplates(agencyId); }
      else toast.error((await r.json()).detail || 'Erreur');
    } catch (e) { toast.error('Erreur d\'enregistrement'); } finally { setSaving(false); }
  };

  const exportJson = () => {
    const data = { name: editing.name, steps };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${editing.name.replace(/\s+/g, '_')}.json`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  const importJson = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result);
        if (!Array.isArray(data.steps)) throw new Error('format');
        const imported = data.steps.map((s) => ({
          step_id: uid('stp'), title: s.title || 'Etape', description: s.description || '', order: s.order || 0,
          fields: (s.fields || []).map((f) => ({ ...f, field_id: uid('fld') })),
        }));
        setSteps(imported);
        if (data.name) setEditing((ed) => ({ ...ed, name: data.name }));
        toast.success('Template importe (pensez a Enregistrer)');
      } catch (err) { toast.error('Fichier JSON invalide'); }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const doDuplicate = async (t) => {
    setDupAgency(agencies.find((a) => a.agency_id !== t.agency_id)?.agency_id || t.agency_id);
    setEditing({ ...t });
    setDupOpen(true);
  };
  const confirmDuplicate = async () => {
    const r = await fetch(`${API}/fdv-templates/${editing.template_id}/duplicate`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ target_agency_id: dupAgency }),
    });
    if (r.ok) { toast.success('Template duplique'); setDupOpen(false); if (dupAgency === agencyId) fetchTemplates(agencyId); }
    else toast.error((await r.json()).detail || 'Erreur');
  };

  if (!isGroupSA) {
    return <div className="p-6 text-slate-500" data-testid="fdv-builder-forbidden">Reserve au Super Admin Groupe.</div>;
  }

  const selectedField = selected?.fieldId && steps.find((s) => s.step_id === selected.stepId)
    ?.fields.find((f) => f.field_id === selected.fieldId);
  const selectedStep = selected?.stepId && steps.find((s) => s.step_id === selected.stepId);

  // ---------- LIST VIEW ----------
  if (!editing || dupOpen) {
    return (
      <div className="space-y-6" data-testid="fdv-builder-page">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-[Manrope]" data-testid="fdv-builder-title">
              Formulaires FDV
            </h1>
            <p className="text-slate-500 mt-1">Construisez les formulaires de declaration de vente par filiale</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={agencyId} onValueChange={setAgencyId}>
              <SelectTrigger className="w-56" data-testid="fdv-builder-agency-select"><SelectValue placeholder="Filiale" /></SelectTrigger>
              <SelectContent>
                {agencies.map((a) => <SelectItem key={a.agency_id} value={a.agency_id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={createTemplate} data-testid="fdv-builder-new-btn"><Plus className="w-4 h-4 mr-2" />Nouveau</Button>
          </div>
        </div>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : templates.length === 0 ? (
              <div className="p-10 text-center text-slate-500">Aucun template pour cette filiale. Cliquez sur "Nouveau".</div>
            ) : (
              <div className="divide-y">
                {templates.map((t) => (
                  <div key={t.template_id} className="flex items-center justify-between p-4" data-testid={`fdv-tpl-row-${t.template_id}`}>
                    <div className="flex items-center gap-3">
                      <FileStack className="w-5 h-5 text-indigo-600" />
                      <div>
                        <p className="font-medium text-slate-900">{t.name}</p>
                        <p className="text-xs text-slate-500">{(t.steps || []).length} etape(s)</p>
                      </div>
                      {(t.assigned_agencies && t.assigned_agencies.length > 0) ? (
                        t.assigned_agencies.map((n) => (
                          <Badge key={n} className="bg-emerald-100 text-emerald-700 border-emerald-200 border">Utilise: {n}</Badge>
                        ))
                      ) : (
                        <Badge variant="outline">Non attribue</Badge>
                      )}
                      {!t.montant_total_placed && (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-200 border">montant_total manquant</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEditor(t.template_id)} data-testid={`fdv-tpl-edit-${t.template_id}`}>
                        <Pencil className="w-4 h-4 mr-1" />Editer
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => doDuplicate(t)} data-testid={`fdv-tpl-dup-${t.template_id}`}>
                        <Copy className="w-4 h-4 mr-1" />Dupliquer
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteTemplate(t)}
                        disabled={(t.assigned_agencies || []).length > 0} data-testid={`fdv-tpl-del-${t.template_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={dupOpen} onOpenChange={setDupOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Dupliquer le template</DialogTitle>
              <DialogDescription>Copier "{editing?.name}" vers une filiale (cree un brouillon inactif).</DialogDescription>
            </DialogHeader>
            <div className="py-2 space-y-2">
              <Label>Filiale cible</Label>
              <Select value={dupAgency} onValueChange={setDupAgency}>
                <SelectTrigger data-testid="fdv-dup-agency-select"><SelectValue placeholder="Filiale" /></SelectTrigger>
                <SelectContent>
                  {agencies.map((a) => <SelectItem key={a.agency_id} value={a.agency_id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDupOpen(false); setEditing(null); }}>Annuler</Button>
              <Button onClick={confirmDuplicate} data-testid="fdv-dup-confirm-btn">Dupliquer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ---------- EDITOR VIEW ----------
  return (
    <div className="space-y-4" data-testid="fdv-builder-editor">
      {/* top bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setEditing(null)} data-testid="fdv-editor-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" />Retour
          </Button>
          <Input value={editing.name} onChange={(e) => setEditing((ed) => ({ ...ed, name: e.target.value }))}
            className="w-64 font-semibold" data-testid="fdv-editor-name-input" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-lg border overflow-hidden">
            <button onClick={() => setTab('build')} className={`px-3 py-1.5 text-sm ${tab === 'build' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600'}`} data-testid="fdv-tab-build">
              <Pencil className="w-4 h-4 inline mr-1" />Construire
            </button>
            <button onClick={() => setTab('preview')} className={`px-3 py-1.5 text-sm ${tab === 'preview' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600'}`} data-testid="fdv-tab-preview">
              <Eye className="w-4 h-4 inline mr-1" />Apercu
            </button>
          </div>
          <label className="cursor-pointer">
            <input type="file" accept="application/json" className="hidden" onChange={importJson} data-testid="fdv-import-input" />
            <span className="inline-flex items-center px-3 py-1.5 text-sm border rounded-lg text-slate-600 hover:bg-slate-50"><Upload className="w-4 h-4 mr-1" />Importer</span>
          </label>
          <Button variant="outline" size="sm" onClick={exportJson} data-testid="fdv-export-btn"><Download className="w-4 h-4 mr-1" />Exporter</Button>
          <Button variant="outline" size="sm" onClick={save} disabled={saving} data-testid="fdv-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Enregistrer
          </Button>
        </div>
      </div>

      {!montantPlaced(steps) && (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm" data-testid="fdv-montant-warning">
          <AlertTriangle className="w-4 h-4" /> Le champ <strong>Montant total</strong> est obligatoire avant de pouvoir attribuer ce formulaire a une filiale (calcul des commissions).
        </div>
      )}

      {tab === 'preview' ? (
        <PreviewForm steps={steps} />
      ) : (
        <div className="grid grid-cols-12 gap-4">
          {/* palette */}
          <div className="col-span-12 md:col-span-3">
            <Card className="border-0 shadow-sm sticky top-4">
              <CardContent className="p-4 space-y-2">
                <p className="text-sm font-semibold text-slate-700 mb-2">Palette de champs</p>
                <p className="text-xs text-slate-400 mb-2">Clic pour ajouter a l'etape selectionnee</p>
                {FIELD_TYPES.map((ft) => {
                  const Icon = ft.icon;
                  return (
                    <button key={ft.type} onClick={() => addField(ft.type)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg border hover:border-indigo-400 hover:bg-indigo-50 transition-colors text-left"
                      data-testid={`fdv-palette-${ft.type}`}>
                      <Icon className="w-4 h-4 text-indigo-600" /> {ft.label}
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* canvas */}
          <div className="col-span-12 md:col-span-6 space-y-3">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onStepDragEnd}>
              <SortableContext items={steps.map((s) => s.step_id)} strategy={verticalListSortingStrategy}>
                {steps.map((step) => (
                  <SortableStep key={step.step_id} step={step} selected={selected}
                    setSelected={setSelected} removeStep={removeStep}
                    sensors={sensors} onFieldDragEnd={onFieldDragEnd} removeField={removeField} />
                ))}
              </SortableContext>
            </DndContext>
            <Button variant="outline" className="w-full border-dashed" onClick={addStep} data-testid="fdv-add-step-btn">
              <Plus className="w-4 h-4 mr-2" />Ajouter une etape
            </Button>
          </div>

          {/* properties */}
          <div className="col-span-12 md:col-span-3">
            <Card className="border-0 shadow-sm sticky top-4">
              <CardContent className="p-4">
                <p className="text-sm font-semibold text-slate-700 mb-3">Proprietes</p>
                {selectedField ? (
                  <FieldProps key={selectedField.field_id} field={selectedField} steps={steps}
                    onChange={(patch) => updateField(selected.stepId, selectedField.field_id, patch)}
                    onMove={(toStep) => moveFieldToStep(selected.stepId, selectedField.field_id, toStep)}
                    currentStep={selected.stepId} />
                ) : selectedStep ? (
                  <StepProps key={selectedStep.step_id} step={selectedStep}
                    onChange={(patch) => updateStep(selectedStep.step_id, patch)} />
                ) : (
                  <p className="text-sm text-slate-400">Selectionnez une etape ou un champ.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Sortable Step ----------
function SortableStep({ step, selected, setSelected, removeStep, sensors, onFieldDragEnd, removeField }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.step_id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const isSel = selected?.stepId === step.step_id && !selected?.fieldId;
  return (
    <div ref={setNodeRef} style={style}>
      <Card className={`border ${isSel ? 'border-indigo-400 ring-1 ring-indigo-200' : 'border-slate-200'} shadow-sm`} data-testid={`fdv-step-${step.step_id}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <button {...attributes} {...listeners} className="cursor-grab text-slate-400" data-testid={`fdv-step-drag-${step.step_id}`}>
              <GripVertical className="w-4 h-4" />
            </button>
            <button className="flex-1 text-left font-semibold text-slate-800" onClick={() => setSelected({ stepId: step.step_id })}>
              {step.title || 'Etape'}
            </button>
            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeStep(step.step_id)} data-testid={`fdv-step-del-${step.step_id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          {(step.fields || []).length === 0 ? (
            <p className="text-xs text-slate-400 py-2">Aucun champ. Selectionnez cette etape puis cliquez un champ dans la palette.</p>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onFieldDragEnd(step.step_id)}>
              <SortableContext items={step.fields.map((f) => f.field_id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {step.fields.map((f) => (
                    <SortableField key={f.field_id} field={f} stepId={step.step_id}
                      selected={selected} setSelected={setSelected} removeField={removeField} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SortableField({ field, stepId, selected, setSelected, removeField }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.field_id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const isSel = selected?.fieldId === field.field_id;
  return (
    <div ref={setNodeRef} style={style}
      className={`flex items-center gap-2 p-2 rounded-lg border ${isSel ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 bg-white'}`}
      data-testid={`fdv-field-row-${field.field_id}`}>
      <button {...attributes} {...listeners} className="cursor-grab text-slate-400"><GripVertical className="w-4 h-4" /></button>
      <button className="flex-1 text-left text-sm" onClick={() => setSelected({ stepId, fieldId: field.field_id })}>
        <span className="font-medium text-slate-800">{field.label}</span>
        {field.required && <span className="text-red-500 ml-1">*</span>}
        <Badge variant="outline" className="ml-2 text-xs">{TYPE_LABEL[field.type] || field.type}</Badge>
      </button>
      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeField(stepId, field.field_id)} data-testid={`fdv-field-del-${field.field_id}`}>
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

function StepProps({ step, onChange }) {
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label className="text-xs">Titre de l'etape</Label>
        <Input value={step.title || ''} onChange={(e) => onChange({ title: e.target.value })} data-testid="fdv-prop-step-title" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Description</Label>
        <Textarea value={step.description || ''} onChange={(e) => onChange({ description: e.target.value })} />
      </div>
    </div>
  );
}

function FieldProps({ field, steps, onChange, onMove, currentStep }) {
  const isMontant = field.type === 'montant_total';
  const hasOptions = field.type === 'select' || field.type === 'radio' || field.type === 'checkbox';
  const isSlider = field.type === 'slider';
  const isDuration = field.type === 'duration';
  const DUR_UNITS = ['Secondes', 'Minutes', 'Heures', 'Jours', 'Semaines', 'Mois', 'Annees'];
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label className="text-xs">Libelle</Label>
        <Input value={field.label || ''} onChange={(e) => onChange({ label: e.target.value })} data-testid="fdv-prop-label" />
      </div>
      {!isMontant && (
        <div className="space-y-1">
          <Label className="text-xs">Cle (technique)</Label>
          <Input value={field.key || ''} onChange={(e) => onChange({ key: slugKey(e.target.value) })} data-testid="fdv-prop-key" />
        </div>
      )}
      <div className="flex items-center justify-between">
        <Label className="text-xs">Obligatoire</Label>
        <Switch checked={!!field.required} disabled={isMontant} onCheckedChange={(c) => onChange({ required: !!c })} data-testid="fdv-prop-required" />
      </div>
      {field.type === 'file' && (
        <div className="flex items-center justify-between">
          <Label className="text-xs">Fichiers multiples</Label>
          <Switch checked={!!field.multiple} onCheckedChange={(c) => onChange({ multiple: !!c })} data-testid="fdv-prop-multiple" />
        </div>
      )}
      {hasOptions && (
        <div className="space-y-1">
          <Label className="text-xs">Options (une par ligne)</Label>
          <Textarea rows={4} value={(field.options || []).join('\n')}
            onChange={(e) => onChange({ options: e.target.value.split('\n').map((o) => o.trim()).filter(Boolean) })}
            data-testid="fdv-prop-options" />
        </div>
      )}
      {isSlider && (
        <div className="grid grid-cols-3 gap-2">
          <div><Label className="text-xs">Min</Label><Input type="number" value={field.min ?? 0} onChange={(e) => onChange({ min: Number(e.target.value) })} /></div>
          <div><Label className="text-xs">Max</Label><Input type="number" value={field.max ?? 100} onChange={(e) => onChange({ max: Number(e.target.value) })} /></div>
          <div><Label className="text-xs">Pas</Label><Input type="number" value={field.step ?? 1} onChange={(e) => onChange({ step: Number(e.target.value) })} /></div>
        </div>
      )}
      {field.type === 'checkbox' && (
        <div className="space-y-1">
          <Label className="text-xs">Nombre max de cases cochables (vide = illimite)</Label>
          <Input type="number" min="1" value={field.max_selections ?? ''}
            onChange={(e) => onChange({ max_selections: e.target.value ? Number(e.target.value) : null })}
            data-testid="fdv-prop-max-selections" />
        </div>
      )}
      {isDuration && (
        <div className="space-y-1">
          <Label className="text-xs">Unites visibles</Label>
          <div className="grid grid-cols-2 gap-1">
            {DUR_UNITS.map((u) => {
              const on = (field.units || []).includes(u);
              return (
                <label key={u} className="flex items-center gap-2 text-sm">
                  <Switch checked={on} onCheckedChange={(c) => {
                    const cur = field.units || [];
                    onChange({ units: c ? [...cur, u] : cur.filter((x) => x !== u) });
                  }} data-testid={`fdv-prop-unit-${u}`} />
                  {u}
                </label>
              );
            })}
          </div>
        </div>
      )}
      <div className="space-y-1">
        <Label className="text-xs">Texte d'aide</Label>
        <Input value={field.help_text || ''} onChange={(e) => onChange({ help_text: e.target.value })} />
      </div>
      {steps.length > 1 && (
        <div className="space-y-1">
          <Label className="text-xs">Deplacer vers l'etape</Label>
          <Select value={currentStep} onValueChange={onMove}>
            <SelectTrigger data-testid="fdv-prop-move-step"><SelectValue /></SelectTrigger>
            <SelectContent>
              {steps.map((s) => <SelectItem key={s.step_id} value={s.step_id}>{s.title || 'Etape'}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

// ---------- Live preview ----------
function PreviewForm({ steps }) {
  const [idx, setIdx] = useState(0);
  const sorted = [...steps].sort((a, b) => (a.order || 0) - (b.order || 0));
  if (sorted.length === 0) return <p className="text-slate-500 text-sm">Ajoutez des etapes pour voir l'apercu.</p>;
  const step = sorted[Math.min(idx, sorted.length - 1)];
  return (
    <Card className="border-0 shadow-sm max-w-3xl mx-auto" data-testid="fdv-preview">
      <CardContent className="p-6 space-y-4">
        <div className="text-sm text-slate-500">Etape {idx + 1} / {sorted.length}</div>
        <h3 className="text-xl font-bold text-slate-900">{step.title}</h3>
        {step.description && <p className="text-slate-500 text-sm">{step.description}</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(step.fields || []).map((f) => (
            <div key={f.field_id} className={['textarea', 'radio', 'file', 'slider', 'montant_total'].includes(f.type) ? 'md:col-span-2' : ''}>
              <Label className="text-sm">{f.label}{f.required && ' *'}</Label>
              <PreviewControl field={f} />
            </div>
          ))}
        </div>
        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" size="sm" disabled={idx === 0} onClick={() => setIdx((i) => i - 1)}>Precedent</Button>
          <Button size="sm" disabled={idx >= sorted.length - 1} onClick={() => setIdx((i) => i + 1)}>Suivant</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PreviewControl({ field: f }) {
  const disabled = true;
  switch (f.type) {
    case 'textarea': return <Textarea disabled placeholder={f.placeholder} className="mt-1" />;
    case 'number': case 'montant_total': return <Input type="number" disabled placeholder="0" className="mt-1" />;
    case 'date': return <Input type="date" disabled className="mt-1" />;
    case 'checkbox': return (f.options && f.options.length) ? (
      <div className="mt-1 space-y-1">{f.options.map((o) => <label key={o} className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" disabled /> {o}</label>)}{f.max_selections ? <p className="text-xs text-slate-400">Max {f.max_selections}</p> : null}</div>
    ) : <div className="mt-1"><input type="checkbox" disabled /> <span className="text-sm text-slate-500">{f.help_text || 'Oui'}</span></div>;
    case 'duration': return (
      <div className="mt-1 flex flex-wrap gap-2">
        {(f.units && f.units.length ? f.units : ['Mois', 'Annees']).map((u) => (
          <div key={u} className="flex flex-col gap-0.5">
            <span className="text-xs text-slate-400">{u}</span>
            <input type="number" disabled placeholder="0" className="w-20 border rounded-md h-9 px-2 text-sm bg-slate-50" />
          </div>
        ))}
      </div>
    );
    case 'radio': return (
      <div className="mt-1 flex flex-wrap gap-3">{(f.options || []).map((o) => <label key={o} className="text-sm text-slate-600"><input type="radio" disabled /> {o}</label>)}</div>
    );
    case 'select': return (
      <select disabled className="mt-1 w-full border rounded-md h-9 px-2 text-sm text-slate-500 bg-slate-50">
        <option>Selectionner</option>{(f.options || []).map((o) => <option key={o}>{o}</option>)}
      </select>
    );
    case 'slider': return <div className="mt-2"><input type="range" disabled min={f.min ?? 0} max={f.max ?? 100} className="w-full" /></div>;
    case 'file': return <div className="mt-1 h-14 border border-dashed rounded-lg flex items-center justify-center text-slate-400 text-sm">Zone de telechargement</div>;
    default: return <Input disabled placeholder={f.placeholder} className="mt-1" />;
  }
}

export default AdminFDVBuilder;
